import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Button, Typography, Box, List, ListItem, Divider, Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// 全件取得をやめたため API フェッチ系
// import { isApiError } from '../../../lib/apiClient';
// import { buildWeeklySearchParams, fetchWeeklyReports } from '../../../api/weeklySearch';

// 検索UIをモーダル内で再利用
import OverwriteSearchArea from './OverwriteSearchArea';


export interface HistoryItem {
  wr_id: number;
  title: string;
  body: string;
  start_date: string; // ISO (YYYY-MM-DD)
  end_date: string;   // ISO (YYYY-MM-DD)
  temporary_flg: number;
  sync_flg: number;
  create_by: string;
  update_by: string;
  team: number;
  department?: number | null;
  approver?: string | null;
  approve_am?: string | null;
  shikimuke?: string | null;
  destination?: string | null;
  lot?: string | null;
  project?: string | null;
  powertrain?: string | null;
  respondents?: string[] | Record<string, any> | string | null;
  respondents_1?: string[] | Record<string, any> | string | null;
  files?: Array<{
    file_id?: number;
    fileId?: number;
    object_key?: string;
    objectKey?: string;
    object_key_hash?: string;
    objectKeyHash?: string;
    original_name?: string;
    originalName?: string;
    signed_url?: string;
    signedUrl?: string;
    url?: string;
    public_url?: string;
    publicUrl?: string;
    status?: string;
    [key: string]: any;
  }>;

  // 動的属性の実データ（OverwriteInputSearchArea がこれを読む）
  attribute_values?:
    | Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }>
    | string
    | null;

  // API が attributes / base_attributes など別名で返す場合もあるため保持する
  attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  attributeValues?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  base_attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  baseAttributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  weekly_report_base_attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;

  status?: string | null;
  feedback_body?: string | null;
}

interface OverwriteMuiModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHistoryItemSelect: (item: HistoryItem) => void;
  onItemsLoaded?: (items: HistoryItem[]) => void;
  onSearchResults?: (results: any[]) => void; // ← 親にも通知したい場合に使う（任意）
}

/** ユーティリティ: 値取り出し（別名キー対応） */
const pick = (obj: any, keys: (string | string[])[], def: any = undefined) => {
  for (const k of keys) {
    if (Array.isArray(k)) {
      for (const kk of k) {
        if (obj && obj[kk] != null) return obj[kk];
      }
    } else if (obj && obj[k] != null) {
      return obj[k];
    }
  }
  return def;
};
const toStr = (v: any): string => (v == null ? '' : String(v));
const toNumOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const toNumOrZero = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** 文字列/配列の attribute_values を配列に正規化（失敗時は null） */
const normalizeAttrVals = (
  raw: any
): Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null => {
  if (!raw) return null;
  try {
    const arr = Array.isArray(raw) ? raw : JSON.parse(String(raw));
    if (!Array.isArray(arr)) return null;
    return arr
      .map((x) => {
        if (x == null) return null;
        if (typeof x === 'string') return null; // "ギアノイズ" のような素の文字列は捨てる
        const attr_id = x.attr_id ?? x.id ?? x.attrId;
        const attr_name =
          x.attr_name ??
          x.attrName ??
          x.attribute_name ??
          x.attributeName ??
          x.name ??
          x.label;
        const attr_value =
          x.attr_value ?? x.attrValue ?? x.value ?? x.text ?? '';
        if (attr_id == null && attr_name == null && !attr_value) return null;
        return {
          attr_id,
          attr_name: attr_name != null ? String(attr_name) : undefined,
          attr_value: attr_value != null ? String(attr_value) : '',
        };
      })
      .filter(Boolean) as Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }>;
  } catch {
    return null;
  }
};

/**
 * 複数候補の中から「中身がある属性配列」を優先して取得する。
 *
 * 重要:
 * API レスポンスに attribute_values: [] と attributes: [...] が同時に来るケースがある。
 * 単純に attribute_values を最優先すると空配列で確定してしまい、
 * DB に登録済みの attributes 側の値が OverwriteInputSearchArea へ届かない。
 */
const pickFirstNonEmptyAttrVals = (rec: any) => {
  const candidates = [
    rec?.attribute_values,
    rec?.attr_values,
    rec?.attributes,
    rec?.attrs,
    rec?.attrValues,
    rec?.attributeValues,
    rec?.base_attributes,
    rec?.baseAttributes,
    rec?.weekly_report_base_attributes,
    rec?.weeklyReportBaseAttributes,
  ];

  let firstEmpty: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null = null;

  for (const raw of candidates) {
    const normalized = normalizeAttrVals(raw);

    if (!normalized) {
      continue;
    }

    if (normalized.length > 0) {
      return normalized;
    }

    if (firstEmpty == null) {
      firstEmpty = normalized;
    }
  }

  return firstEmpty;
};

/** /weekly-report-overwrite の（不定形の）結果を HistoryItem[] に正規化 */
const normalizeOverwriteResults = (list: any[]): HistoryItem[] => {
  if (!Array.isArray(list)) return [];
  return list.map((rec, i) => {
    const wrId =
      Number(pick(rec, ['wr_id', 'wrId', 'weekly_report_id', 'weeklyReportId', 'id'], i + 1)) ||
      (i + 1);

    const startDate = toStr(pick(rec, ['start_date', 'startDate', 'start'], ''));
    const endDate = toStr(pick(rec, ['end_date', 'endDate', 'end'], ''));
    const title = toStr(pick(rec, ['title', ['subject', 'name']], ''));
    const body = toStr(pick(rec, ['body', 'content', 'text'], ''));
    const feedback_body = toStr(pick(rec, ['feedback_body', 'feedbackBody', 'feedback'], ''));
    const create_by = toStr(
      pick(rec, ['create_by', 'createBy', 'created_by', 'createdBy', 'author', 'writer'], '')
    );
    const update_by = toStr(
      pick(rec, ['update_by', 'updateBy', 'updated_by', 'updatedBy', 'editor'], '')
    );

    const team = toNumOrZero(pick(rec, ['team', 'team_id', 'teamId'], 0));
    const department = toNumOrNull(pick(rec, ['department', 'department_id', 'departmentId'], null));
    const approver = toStr(pick(rec, ['approver', 'approve_3', 'approve3'], ''));
    const approve_am = toStr(pick(rec, ['approve_am', 'approveAm'], ''));
    const shikimuke = toStr(pick(rec, ['shikimuke', 'destination', 'Shimuke_1', 'Shimuke', 'destination_1'], ''));
    const destination = toStr(pick(rec, ['destination', 'shikimuke', 'Shimuke_1', 'Shimuke', 'destination_1'], ''));
    const lot = toStr(pick(rec, ['lot', 'lot_1', 'Lot_1', 'Lot'], ''));
    const project = toStr(pick(rec, ['project', 'project_1', 'Project_1', 'Project'], ''));
    const powertrain = toStr(pick(rec, ['powertrain', 'powerTrain', 'powertrain_1', 'powerTrain_1'], ''));
    const respondents_1 = (pick(rec, ['respondents_1', 'respondents1'], null) ?? null) as
      | Record<string, any>
      | string[]
      | string
      | null;
    const respondents = (pick(rec, ['respondents'], null) ?? respondents_1 ?? null) as
      | Record<string, any>
      | string[]
      | string
      | null;

    // 検索結果にも temporary_flg / sync_flg を通す
    const temporary_flg = toNumOrZero(
      pick(rec, ['temporary_flg', 'temporaryFlg', 'temporary', 'is_temporary', 'draft', 'is_draft'], 0)
    );
    const sync_flg = toNumOrZero(pick(rec, ['sync_flg', 'syncFlg'], 0));

    // いろいろなキー名に対応して attribute_values を取り込む。
    // 空の attribute_values より、値が入っている attributes/base_attributes を優先する。
    const attribute_values = pickFirstNonEmptyAttrVals(rec);
    const files = Array.isArray((rec as any)?.files) ? ((rec as any).files as HistoryItem['files']) : undefined;

    return {
      wr_id: wrId,
      title,
      body,
      feedback_body,
      start_date: startDate,
      end_date: endDate,
      create_by,
      update_by,
      team,
      department,
      approver,
      approve_am,
      shikimuke,
      destination,
      lot,
      project,
      powertrain,
      respondents,
      respondents_1,
      attribute_values,
      attributes: attribute_values,
      attributeValues: attribute_values,
      base_attributes: attribute_values,
      baseAttributes: attribute_values,
      weekly_report_base_attributes: attribute_values,
      temporary_flg,
      sync_flg,
      files,
    } as HistoryItem;
  });
};

const OverwriteModal: React.FC<OverwriteMuiModalProps> = ({
  isOpen,
  onClose,
  onHistoryItemSelect,
  onItemsLoaded,
  onSearchResults,
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);


  // 検索で上書き表示中かどうか
  const [didSearch, setDidSearch] = useState(false);

  // YYYY/MM/DD - YYYY/MM/DD
  const formatRange = (s: string, e: string) => {
    const sd = new Date(s);
    const ed = new Date(e);
    const fmt = (d: Date) =>
      isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const left = fmt(sd);
    const right = fmt(ed);
    return left && right ? `${left} - ${right}` : '';
  };

  // 親での“全件履歴取得”は撤廃。開いたらローディングだけを出し、子の初回検索完了を待つ。
  useEffect(() => {
    if (!isOpen) {
      // Close 時に初期化（次回オープンは“絞り込み済みの初回検索”から）
      setItems([]);
      setErrorMsg(null);
      setDidSearch(false);
      setLoading(false);
      return;
    }
    // Open 直後はローディング表示に固定（子が onSearchResults を呼ぶまで）
    setItems([]);
    setErrorMsg(null);
    setDidSearch(false);
    setLoading(true);
  }, [isOpen]);

  // 検索ハンドラ：検索結果でモーダル下部リストを上書き（= 子の結果のみを表示）
  const handleSearchResults = (results: any[]) => {
    try {
      const normalized = normalizeOverwriteResults(results); // attribute_values / temporary_flg / sync_flg を通す
      setItems(normalized);
      setDidSearch(true);
      setErrorMsg(null);
      setLoading(false); // 子の検索完了でローディング解除
      onItemsLoaded?.(normalized);
    } catch (e: any) {
      setErrorMsg(e?.message ?? '検索結果の処理に失敗しました。');
      setLoading(false);
    } finally {
      // 親にも結果を流したい場合のみ
      onSearchResults?.(results);
    }
  };

  const modalTitle = useMemo(() => (didSearch ? '検索結果' : '週報連（新しい順）'), [didSearch]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby="history-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: { xs: 1, sm: 1.5 },
          boxShadow: '0 12px 30px -10px rgba(0,0,0,0.2)',
          width: '100vw',
          maxWidth: 1800,
        },
      }}
    >
      <DialogTitle id="history-dialog-title" sx={{ pb: 1, pt: 2, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <HistoryIcon sx={{ color: 'primary.main', mr: 1.5, fontSize: '2rem' }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {modalTitle}
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} sx={{ color: (t) => t.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 0,
          borderTop: isDark ? '1px solid #334155' : '1px solid rgba(0,0,0,0.08)',
          // DialogContent は内部でスクロールが発生（overflowY: auto）。
          // position: sticky はこのスクロール領域に対して効く。
          position: 'relative',
        }}
      >
        {/* 検索 UI（常に上部に固定：sticky） */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (t) => t.zIndex.appBar, // 上に重ねる
            mb: 1.5,
            mx: { xs: '-8px', sm: '-12px' },
            px: { xs: '8px', sm: '12px' },
            py: { xs: 0.5, sm: 1 },
            backgroundColor: isDark ? 'rgba(22, 31, 46, 0.98)' : 'rgba(249, 249, 251, 0.98)',
            backdropFilter: 'saturate(1.2) blur(2px)',
            borderBottom: isDark ? '1px solid #334155' : '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 6px 12px -8px rgba(0,0,0,0.18)',
          }}
        >
          <OverwriteSearchArea
            isOpen={isOpen}
            onSearchResults={handleSearchResults}
          />
        </Box>

        {loading && (
          <Typography variant="body1" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            読み込み中…
          </Typography>
        )}

        {!loading && errorMsg && (
          <Typography variant="body1" sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            {errorMsg}
          </Typography>
        )}

        {!loading && !errorMsg && items.length === 0 && (
          <Typography variant="body1" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            表示する{didSearch ? '検索結果' : '週報'}がありません。
          </Typography>
        )}

        {!loading && !errorMsg && items.length > 0 && (
          <List disablePadding>
            {items.map((item, index) => (
              <React.Fragment key={`${item.wr_id}-${item.start_date}-${item.end_date}-${index}`}>
                <ListItem
                  onClick={() => onHistoryItemSelect(item)}
                  sx={{
                    py: 2,
                    px: { xs: 1, sm: 1.5 },
                    transition: 'background-color 0.2s ease-in-out, transform 0.1s ease-in-out',
                    borderRadius: '8px',
                    m: { xs: '8px 0', sm: '8px' },
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: (t) => t.palette.action.hover,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box sx={{ minWidth: { xs: '160px', sm: '180px' }, mr: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatRange(item.start_date, item.end_date)}
                      </Typography>
                      {item.temporary_flg === 1 ? (
                        <Chip size="small" label="下書き" color="primary" sx={{ height: 20, mt: 0.5, fontWeight: 600 }} />
                      ) : (
                        <Chip size="small" label="週報連" color="success" sx={{ height: 20, mt: 0.5, fontWeight: 600 }} />
                      )}
                    </Box>
                    <Box display="flex" alignItems="baseline" flexWrap="wrap">
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.4, mr: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.4, mr: 1 }}>
                        （作成: {item.create_by || '-'}）
                      </Typography>
                    </Box>
                  </Box>
                  <ArrowForwardIosIcon sx={{ fontSize: '1rem', color: 'grey.400', ml: 1 }} />
                </ListItem>
                {index < items.length - 1 && (
                  <Divider variant="inset" component="li" sx={{ mx: { xs: 0, sm: 2 } }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: '16px 24px', borderTop: isDark ? '1px solid #334155' : '1px solid rgba(0,0,0,0.08)' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#e53935',
            borderColor: '#e53935',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            borderRadius: '8px',
            '&:hover': { backgroundColor: 'rgba(229, 57, 53, 0.04)', borderColor: '#c62828' },
          }}
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OverwriteModal;
